import { all, call, put, select, takeLatest } from "redux-saga/effects";
import { toast } from "react-toastify";
import io from "socket.io-client";
import { getItem } from "../../utils";
import { TOKEN } from "../../constants/localStorage/localStorage.constants";
// Action types
import * as types from "../actionTypes/app.types";

// Custom history
import history from "../../history";

// Calls
import {
  fetchAuthenticatedUserRequest,
  loginUserRequest,
  registerUserRequest,
  updateProfileRequest
} from "../../api/calls/app.call";
import { fetchSystemComponents } from "../../api/calls/system-components.call";
import {
  createBranchRequest,
  fetchBranchesRequest,
  updateBranchRequest
} from "../../api/calls/branch.call";
import { fetchOnboardingStatusRequest } from "../../api/calls/general-branch-settings.call";

// Actions
import {
  fetchAuthenticatedUserDataSuccess,
  fetchSystemComponentsSuccess,
  registerSuccess,
  updateProfileSuccess,
  loginSuccess,
  createBranchSuccess,
  fetchBranchesSuccess,
  updateBranchSuccess,
  fetchOnboardingStatusSuccess,
  connectToSocketSuccess
} from "../actions/app.actions";
import { WEB_SOCKET_URL } from "../../constants/api/api.constants";

import { fetchSaga } from "./fetch.saga";

// Fetch authenticated user details — fails silently (no toast on app boot).
function* getAuthenticatedUserData() {
  const response = yield call(fetchAuthenticatedUserRequest);
  if (response.hasError) {
    return yield put({
      type: types.FETCH_AUTHENTICATED_USER_FAILED
    });
  }
  yield put(fetchAuthenticatedUserDataSuccess(response));
}

// Register user — redirects home after success.
function* register(action) {
  const response = yield call(registerUserRequest, action.registerFormValues);
  if (response.hasError) {
    toast.error(response.error.message);
    return yield put({
      type: types.REGISTER_FAILED
    });
  }
  yield put(registerSuccess(response));
  history.replace("/");
}

// Create branch — refetches the authenticated user afterwards so
// branch-related menu items appear and the new branch becomes active
// without a manual page refresh.
function* createBranch(action) {
  const response = yield call(createBranchRequest, action.branchFormValues);
  if (response.hasError) {
    toast.error(response.error.message);
    return yield put({
      type: types.CREATE_BRANCH_FAILED
    });
  }
  yield put(createBranchSuccess(response.branch));
  yield call(getAuthenticatedUserData);
}

// Fetch onboarding status — backs the dashboard setup checklist. Fails
// silently — the checklist just won't update rather than surfacing an
// error toast on the dashboard.
function* getOnboardingStatus() {
  // Onboarding completion is permanent once the backend has stamped it, so
  // there is nothing left to poll for. Reducers run before saga workers, so the
  // dashboardData carried by LOGIN / REGISTER / FETCH_AUTHENTICATED_USER_SUCCESS
  // is already in the store by the time this guard reads it. The activation
  // response deliberately does not carry the stamp, which is what lets the
  // just-completed session make one more round trip and show the "you're live"
  // card before this goes quiet.
  const completedAt = yield select(
    state => state.app.dashboardData?.onboardingCompletedAt
  );
  if (completedAt) {
    return;
  }

  const response = yield call(fetchOnboardingStatusRequest);
  if (response.hasError) {
    return yield put({
      type: types.FETCH_ONBOARDING_STATUS_FAILED
    });
  }
  yield put(fetchOnboardingStatusSuccess(response));
}

// connect to socket logic
function* connectToSocketCall() {
  const socket = io.connect(WEB_SOCKET_URL, {
    auth: callback => callback({ token: getItem(TOKEN) })
  });

  yield put(connectToSocketSuccess(socket));
}

export default function* appSaga() {
  yield all([
    takeLatest(types.FETCH_AUTHENTICATED_USER_REQUEST, getAuthenticatedUserData),
    takeLatest(types.REGISTER_REQUEST, register),
    takeLatest(types.CREATE_BRANCH_REQUEST, createBranch),
    takeLatest(types.FETCH_ONBOARDING_STATUS_REQUEST, getOnboardingStatus),
    // Fetch onboarding status right after auth so the side menu and route
    // gating (useOnboardingGate) work without the Dashboard mounting first.
    // Reducers run before workers, so ACTIVE_BRANCH is already in
    // localStorage and the request carries the x-branch header.
    takeLatest(
      [
        types.LOGIN_SUCCESS,
        types.REGISTER_SUCCESS,
        types.FETCH_AUTHENTICATED_USER_SUCCESS
      ],
      getOnboardingStatus
    ),
    takeLatest(types.FETCH_CHAT_AUTH_TOKEN_REQUEST, connectToSocketCall),
    fetchSaga({
      trigger: types.LOGIN_REQUEST,
      apiCall: loginUserRequest,
      args: a => [a.loginFormValues],
      onSuccess: r => loginSuccess(r),
      failedType: types.LOGIN_FAILED
    })(),
    fetchSaga({
      trigger: types.UPDATE_PROFILE_REQUEST,
      apiCall: updateProfileRequest,
      args: a => [a.profileFormValues, a.initialProfileData],
      onSuccess: r => updateProfileSuccess(r),
      failedType: types.UPDATE_PROFILE_FAILED
    })(),
    fetchSaga({
      trigger: types.FETCH_SYSTEM_COMPONENTS,
      apiCall: fetchSystemComponents,
      args: a => [a.queryParams],
      onSuccess: r => fetchSystemComponentsSuccess(r),
      failedType: types.FETCH_SYSTEM_COMPONENTS_FAILED
    })(),
    fetchSaga({
      trigger: types.UPDATE_BRANCH_REQUEST,
      apiCall: updateBranchRequest,
      args: a => [a.branchFormValues, a.initialValues, a.id],
      onSuccess: r => updateBranchSuccess(r.branch),
      failedType: types.UPDATE_BRANCH_FAILED
    })(),
    fetchSaga({
      trigger: types.FETCH_BRANCHES_REQUEST,
      apiCall: fetchBranchesRequest,
      onSuccess: r => fetchBranchesSuccess(r.branches),
      failedType: types.FETCH_BRANCHES_FAILED
    })()
  ]);
}
